import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Users, Rss } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { HelpfulButton } from "@/components/HelpfulButton";
import { PhotoStrip } from "@/components/ReviewPhotos";
import { TravelerSearch } from "@/components/TravelerSearch";
import { CommentThread } from "@/components/CommentThread";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function Following() {
  const [feed, setFeed] = useState(null);
  const load = () => api.get("/feed").then((r) => setFeed(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  if (!feed) return <main className="min-h-[60vh]" />;

  return (
    <main data-testid="following-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <p className="text-xs uppercase tracking-[0.2em] text-[#FFB800]">Your people</p>
      <h1 className="text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3"><Rss className="h-7 w-7 text-[#FF3B6B]" /> Following</h1>

      <TravelerSearch />
      {feed.following.length > 0 && (
        <div className="mt-6 flex gap-2 flex-wrap">
          {feed.following.map((p) => (
            <Link key={p.id} to={`/traveler/${p.id}`} data-testid={`following-user-${p.id}`}
              className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-[#151C2C] border border-[#26334D] hover:border-[#FFB800] transition-colors text-sm">
              <Avatar url={p.avatar_url} name={p.name} size="h-7 w-7" />{p.name}
            </Link>
          ))}
        </div>
      )}

      {feed.following.length === 0 ? (
        <div data-testid="feed-empty" className="mt-10 rounded-2xl border border-dashed border-[#26334D] p-12 text-center text-slate-400">
          <Users className="h-8 w-8 mx-auto mb-3 text-slate-500" />
          You're not following anyone yet. Search for friends above, or tap a reviewer's name on any listing, then hit <span className="text-[#FFB800] font-semibold">Follow</span>.
          <div className="mt-5"><Link to="/explore" data-testid="feed-explore-link" className="px-5 py-2.5 rounded-full bg-[#FF3B6B] text-white font-semibold">Find reviewers</Link></div>
        </div>
      ) : feed.reviews.length === 0 ? (
        <p data-testid="feed-no-reviews" className="mt-10 text-center text-slate-400">The travelers you follow haven't posted reviews yet.</p>
      ) : (
        <div className="mt-8 space-y-4">
          {feed.reviews.map((r, i) => (
            <article key={r.id} data-testid={`feed-item-${r.id}`} className="rise rounded-2xl bg-[#151C2C] border border-[#26334D] p-5" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Link to={`/traveler/${r.author_id}`} data-testid={`feed-author-${r.id}`} className="flex items-center gap-2 text-sm font-semibold hover:text-[#FFB800]">
                  <Avatar url={r.author_avatar} name={r.user_name} />{r.user_name}
                </Link>
                <span className="text-xs text-slate-500">{new Date(r.updated_at).toLocaleDateString()}</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <p className="font-semibold">{r.place_name}{r.city && <span className="text-sm text-slate-500 font-normal"> · {r.city}</span>}</p>
                <span className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-[#FFB800] text-[#FFB800]" : "text-slate-600"}`} />)}</span>
              </div>
              {r.text && <p className="text-sm text-slate-300 mt-2">{r.text}</p>}
              <PhotoStrip photos={r.photos} rid={r.id} />
              {r.city && (
                <Link to={`/explore?city=${encodeURIComponent(r.city)}`} data-testid={`feed-city-${r.id}`} className="inline-block mt-3 text-xs text-[#00E5A3] hover:underline">See more in {r.city} →</Link>
              )}
              <HelpfulButton review={r} onVoted={load} />
              <CommentThread kind="review" target={r.id} count={r.comment_count} tid={`feed-${r.id}`} />
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
